// User Preferences API
import api, { type AxiosResponse } from './client';
import type {
  UserPreferences,
  EffectivePreferences,
  PreferencesChoices,
  CompanyPreferences,
  SiteRegionalSettings,
  GroupRegionalDefaults,
  GroupRegionalDefaultsUpdate,
} from './types';

/**
 * Per-user Parts Master column manager state.
 *
 * `visible` is the set of column ids the user wants rendered. `order` is the
 * full ordering (sticky-left columns first, then user-arranged). Both are
 * optional on PATCH — sending only one preserves the other.
 */
export interface PartsMasterColumnState {
  visible: string[];
  order: string[];
}

export const preferencesAPI = {
  // Get user preferences
  getUserPreferences: (): Promise<AxiosResponse<UserPreferences>> =>
    api.get('/core/preferences/'),

  // Update user preferences
  updateUserPreferences: (data: Partial<UserPreferences>): Promise<AxiosResponse<UserPreferences>> =>
    api.patch('/core/preferences/', data),

  // Get effective (computed) preferences
  getEffectivePreferences: (): Promise<AxiosResponse<EffectivePreferences>> =>
    api.get('/core/preferences/effective/'),

  // Get view-based effective preferences (based on selected site/company/group)
  getViewEffectivePreferences: (params: {
    site?: number | null;
    company?: number | null;
    group?: number | null;
  }): Promise<AxiosResponse<EffectivePreferences>> => {
    const queryParams = new URLSearchParams();
    if (params.site) queryParams.append('site', params.site.toString());
    if (params.company) queryParams.append('company', params.company.toString());
    if (params.group) queryParams.append('group', params.group.toString());
    const queryString = queryParams.toString();
    return api.get(`/core/preferences/view-effective/${queryString ? `?${queryString}` : ''}`);
  },

  // Get available choices for dropdowns
  getPreferencesChoices: (): Promise<AxiosResponse<PreferencesChoices>> =>
    api.get('/core/preferences/choices/'),

  // Get company preferences
  getCompanyPreferences: (): Promise<AxiosResponse<CompanyPreferences>> =>
    api.get('/core/preferences/company/'),

  // Update company preferences (admin only)
  updateCompanyPreferences: (data: Partial<CompanyPreferences>): Promise<AxiosResponse<CompanyPreferences>> =>
    api.patch('/core/preferences/company/', data),

  // Get site regional settings list
  getSiteRegionalSettings: (): Promise<AxiosResponse<SiteRegionalSettings[]>> =>
    api.get('/core/site-regional-settings/'),

  // CR-6256: group defaults that inheriting sites resolve to, scoped to the
  // same companies the site list returns.
  getGroupRegionalDefaults: (): Promise<AxiosResponse<GroupRegionalDefaults>> =>
    api.get('/core/site-regional-settings/group-defaults/'),

  // CR-6256: set the group's own defaults. Null on a field clears it, so that
  // field falls back to the parent group or the platform default.
  updateGroupRegionalDefaults: (
    data: GroupRegionalDefaultsUpdate,
  ): Promise<AxiosResponse<GroupRegionalDefaults>> =>
    api.patch('/core/site-regional-settings/group-defaults/', data),

  // Get site regional settings by ID
  getSiteRegionalSettingsById: (id: number): Promise<AxiosResponse<SiteRegionalSettings>> =>
    api.get(`/core/site-regional-settings/${id}/`),

  // Update site regional settings
  updateSiteRegionalSettings: (id: number, data: Partial<SiteRegionalSettings>): Promise<AxiosResponse<SiteRegionalSettings>> =>
    api.patch(`/core/site-regional-settings/${id}/`, data),

  // Parts Master column manager — per-user visibility + ordering.
  getPartsMasterColumnState: (): Promise<AxiosResponse<PartsMasterColumnState>> =>
    api.get('/core/preferences/parts-master-columns/'),

  updatePartsMasterColumnState: (
    data: Partial<PartsMasterColumnState>,
  ): Promise<AxiosResponse<PartsMasterColumnState>> =>
    api.patch('/core/preferences/parts-master-columns/', data),
};
